import { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styled, { useTheme } from "styled-components/macro";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import {
  ButtonWithConfirmDialog,
  DestructiveButtonMediumV2,
  GoBackButtonSmall,
  InvisibleButton,
  PrimaryButtonMediumV2,
} from "../../../components/Buttons/Buttons";
import { PageHeader, PageTitle } from "../../../layout/portalPageLayout";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import { Tabs } from "@reach/tabs";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import useSWR from "swr";
import { useAuthContext } from "../../../components/Auth";
import { CompletionScore } from "../../../components/CompletionScore/CompletionScore";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../components/Error";
import {
  HeaderContainer,
  SubheaderContainer,
  SubheaderInformationContainer,
} from "../../../components/Layout/Layout";
import { LinkUnstyled } from "../../../components/LinkUnstyled/LinkUnstyled";
import { Modal } from "../../../components/Modal/Modal";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusLeft,
} from "../../../components/Status/Status";
import {
  InnerTitleText,
  SubheaderContentText,
} from "../../../components/Typography/Typography";
import { endpoints } from "../../../endpoints";
import type {
  StorefrontMetaData,
  DataMutate,
  WithPagination,
} from "../../../types/types";
import type {
  Assets,
  PIMProduct,
  ProductListPriceSchema,
  ProductStatusType,
  TdsGeneratedAssetSchema,
} from "../../../types/types.PIM";
import { useRoutePath } from "../../../util/Routing";
import {
  makeUrlWithParams,
  useMediaQueries,
  useStoreState,
} from "../../../util/util";
import type { IProductVisibilityData } from "./Connections/Connections";
import { ProductShareButton } from "./ProductShareButton";
import { ProductTabs } from "./util";
import moment from "moment";
import {
  CalendarIcon,
  CardCompletionBadgeIcon,
  MasterProductIcon,
  TeamsIcon,
  TemplatesNavIcon,
} from "../../../components/Icons/Icons";
import {
  Breadcrumb,
  FullwidthPageWrapper,
} from "../../../components/ProductsLayout/SharedProductsLayout";
import { Content } from "../../../components/AccordionCard/AccordionCard";
import { screenSize } from "../../../theme";

export const ProductHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  top: 0;
  z-index: 3;
  background-color: ${({ theme }) => theme.resolve("color_neutral_white")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-bottom: 0;
  @media (${screenSize.small}) {
    position: relative;
  }
`;

export const ProductHeader = styled(PageHeader)`
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 10px;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
`;

const ProductHeaderImage = styled.img`
  min-width: 100px;
  max-width: 200px;
  max-height: 150px;
  margin-right: 15px;
  border: 1px solid ${({ theme }) => theme.primaryBorder};
  border-radius: 8px;
  padding: 1px;
  align-self: center;
  cursor: pointer;
`;

const ProductHeaderActions = styled.div`
  justify-self: end;
  display: flex;
  min-width: 100px;
  gap: 16px;
  align-items: center;
  button {
    margin-bottom: 0;
  }
`;
const ScoreLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.secondaryTextColor};
`;
const ContentContainer = styled.div<{
  pageContainerSettings: string;
  productBackground: string;
  headerHeight: number;
}>`
  flex: 1;
  background-color: ${({ theme, pageContainerSettings, productBackground }) =>
    pageContainerSettings === "no_containers" || !productBackground
      ? theme.resolve("color_bg_surface_product")
      : productBackground};
  [class*="TabList"] {
    padding: ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_xl")} 0;
    max-width: 100%;
    width: 100%;
    z-index: 3;
    position: sticky;
    background: #fff;
    top: 0px;
  }
  div[data-reach-tabs] {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
  div[data-reach-tab-panels] {
    flex: 1;
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`};
  }
  div[data-reach-tab-panel] {
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`
        : 0};
    border-radius: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? theme.resolve("radius_productDetail_card")
        : 0};
    border: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? `${theme.resolve("border_md")} solid ${theme.resolve(
            "color_border_lighter"
          )}`
        : "none"};
    background-color: ${({ pageContainerSettings }) =>
      pageContainerSettings === "single_container" ? "#fff" : "none"};
  }
  div.product-group-card,
  div.product-collection-card,
  div.product-assets-table,
  div.product-connections,
  div.product-activity,
  div.product-details-section {
    background-color: #fff;
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : pageContainerSettings === "single_container"
        ? `${theme.resolve("spacing_2xl")} ${theme.resolve(
            "spacing_2xl"
          )} ${theme.resolve("spacing_lg")}`
        : theme.resolve("spacing_2xl")};
    border-radius: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : theme.resolve("radius_productDetail_card")};
    border: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? `${theme.resolve("border_md")} solid ${theme.resolve(
            "color_border_lighter"
          )}`
        : "none"};
    margin-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? 0
        : theme.resolve("spacing_3xl")};
  }
  div${Content} {
    border-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? "0"
        : `1px solid ${theme.secondaryBorder}`};
    padding-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? "0"
        : theme.resolve("spacing_2xl")};
  }
  div.product-collection-card,
  div.product-connections,
  div.product-activity {
    h3 {
      margin-top: 0 !important;
    }
  }
`;
const ModalWrapper = styled.div`
  padding: 20px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaddedPrimaryButton = styled(PrimaryButtonMediumV2)`
  padding: 8px 16px;
  white-space: nowrap;
  svg {
    vertical-align: bottom;
  }
  margin-bottom: 5px;
`;

const PaddedDestructiveButton = styled(DestructiveButtonMediumV2)`
  padding: 8px 16px;
  white-space: nowrap;
  svg {
    vertical-align: bottom;
  }
  margin-bottom: 5px;
`;

export type ProductMutate = DataMutate<PIMProduct>;

/**
 * Product detail page. Used for seller admin.
 */
export const ProductDetailPage = () => {
  const { tenant_id, storefront_id, storeDispatch, slug, storefront_metadata } =
    useStoreState();

  const pageContainerSettings =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.container_settings?.value || "no_containers";

  const { hasPermission, roleIsGuest, roleIsSomeKindOfBuyer } =
    useAuthContext();

  const { search, pathname } = useLocation();

  const { product_id: productId } = useParams<{ product_id: string }>();

  const { adminPath } = useRoutePath();

  const { t } = useTranslation();

  const { notifyError, notifySuccess } = useContext(Notifications);

  const history = useHistory();
  const theme = useTheme();
  const [loadingState, setLoadingState] = useState<ProductStatusType>();
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  const [fullSizeProductImage, setFullSizeProductImage] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [teamName, setTeamName] = useState<string>("");
  const [visibilityData, setVisibilityData] =
    useState<IProductVisibilityData>();

  // URL query parameters for tab management
  const [query, setQuery] = useQueryParams({
    tab: NumberParam,
    documentType: StringParam,
  });
  const { data: metadata } = useSWR<StorefrontMetaData>(
    endpoints.v1_storefronts_id_metadata(storefront_id)
  );
  // We cant annotate the type inline while destucturing but "mutate"
  // here is type ProductMutate
  const {
    data: product,
    error: productError,
    mutate,
  } = useSWR<PIMProduct, AxiosError>(
    productId
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, productId),
          {
            use_database: "true",
            return_staged: "true",
          }
        )
      : null,
    { revalidateOnFocus: false }
  );

  // Fetch list prices to determine if the pricing tab should be shown
  const { data: listPricesData } = useSWR<
    WithPagination<{ data: ProductListPriceSchema[] }>
  >(
    product
      ? makeUrlWithParams(
          endpoints.v2_tenants_tenant_id_products_product_id_list_prices(
            tenant_id,
            product.id
          ),
          { limit: 1, offset: 0 }
        )
      : null
  );

  const show_pricing_tab = (listPricesData?.pagination?.total ?? 0) > 0;

  const { isSmallScreen } = useMediaQueries();
  const [headerHeight, setHeaderHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const handleTabChange = async (index: number) => {
    setCurrentTab(index);
    // Convert to 1-based index for URL (tabs are 1-indexed in URLs)
    // Clear documentType when user manually changes tabs
    setQuery({ tab: index + 1, documentType: undefined });
  };
  const goBackToProductsList = () => {
    history.push(
      `${adminPath}/pim/products${
        sessionStorage.getItem("products_search") ?? ""
      }`
    );
  };

  useEffect(() => {
    if (product && ref?.current) {
      setHeaderHeight(ref.current.clientHeight);
    }
  }, [product, ref]);

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (!window.location.pathname.includes("/pim/products")) {
          sessionStorage.removeItem("selectedRows");
        }
      }, 0);
    };
  }, [pathname]);

  useEffect(() => {
    function handleResize() {
      if (product && ref?.current) {
        setHeaderHeight(ref.current.clientHeight);
      }
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    const teamNamesString = (() => {
      const teams = product?.team_names;
      const length = teams?.length;

      if (!length) {
        return t("None");
      } else if (length <= 2) {
        return teams.join(", ");
      } else {
        const firstTwo = teams.slice(0, 2);
        return `${firstTwo.join(", ")} + ${length - 2} ${t("more")}`;
      }
    })();
    setTeamName(teamNamesString);
  }, [product?.team_names, t]);

  // Handle URL tab parameter changes
  useEffect(() => {
    if (query.tab) {
      // Convert from 1-based URL parameter to 0-based tab index
      const tabIndex = query.tab - 1;
      if (tabIndex >= 0 && tabIndex !== currentTab) {
        setCurrentTab(tabIndex);
      }
    }
  }, [query.tab, currentTab]);

  // Handle documentType parameter - automatically find and navigate to documents tab
  useEffect(() => {
    if (query.documentType && product) {
      // Find the documents tab index
      let documentsTabIndex = -1;
      let currentIndex = 0;

      // Iterate through the product schema layout to find documents tab
      for (const tab of product.product_schema.layout || []) {
        if (!tab.is_enabled) continue;

        if (tab.tab_type === "documents") {
          // Check if documents tab should be visible (basic check)
          const hasDocuments =
            product.number_of_assets > 0 ||
            product.number_of_generated_assets > 0 ||
            product.asset_external_links.length > 0;

          if (hasDocuments || hasPermission("view_assets")) {
            documentsTabIndex = currentIndex;
            break;
          }
        }

        // Count tabs that would be rendered (simplified logic)
        if (
          tab.tab_type === "documents" ||
          tab.tab_type === "seo" ||
          tab.tab_type === "sku" ||
          tab.tab_type === "overview" ||
          tab.tab_type === "custom"
        ) {
          currentIndex++;
        }
      }

      // Add hardcoded tabs that appear in non-portfolio mode
      if (!roleIsGuest && !roleIsSomeKindOfBuyer) {
        // Access tab (if user has modify_products permission)
        if (hasPermission("modify_products")) {
          currentIndex++;
        }
        // Connections tab
        currentIndex++;
        // Pricing tab (if applicable)
        const has_sku_values = product.product_skus?.some(
          (sku) => !sku.is_sample
        );
        if (has_sku_values && metadata?.enable_list_prices) {
          currentIndex++;
        }
        // Activity tab
        currentIndex++;
      }

      if (documentsTabIndex !== -1 && documentsTabIndex !== currentTab) {
        setCurrentTab(documentsTabIndex);
        // Update URL to reflect the tab change, but preserve documentType
        setQuery({
          tab: documentsTabIndex + 1,
          documentType: query.documentType,
        });
      }
    }
  }, [
    query.documentType,
    product,
    currentTab,
    hasPermission,
    roleIsGuest,
    roleIsSomeKindOfBuyer,
    metadata?.enable_list_prices,
    setQuery,
  ]);

  if (productError) {
    return (
      <>
        <InvisibleButton onClick={() => goBackToProductsList()}>
          <GoBackButtonSmall text={t("Products")} />
        </InvisibleButton>
        <ErrorPlaceholder message={"There was an error loading the product."} />
      </>
    );
  }
  if (!product) {
    // Product data is still loading.
    return <DelayedSpinner />;
  }

  // const productModifiedAt = formatDateTime(
  //   product.last_modified_full?.modified_at
  // );
  // const productModifiedBy = product.last_modified_full?.modified_by;

  const replaceProductId = async (productId: string) => {
    history.replace(
      search.includes("source=new")
        ? `${adminPath}/pim/products/${productId}?source=new`
        : `${adminPath}/pim/products/${productId}`
    );
    await mutate();
  };

  const productCoverImage = product?.cover_image?.signed_url;

  const pollUntilFinished = async (
    url: string,
    /**
     * Polling interval in milliseconds
     */
    interval: number = 2000,
    /**
     * Maximum time to wait before giving up in milliseconds
     */
    timeout: number = 30000
  ): Promise<AxiosResponse<Assets>> => {
    const startTime = Date.now();

    const checkStatus = async (): Promise<AxiosResponse<Assets>> => {
      const response = await axios.get(url);
      let timeout_id: NodeJS.Timeout | undefined = undefined;

      if (
        response.data.status === "finished" ||
        Date.now() - startTime > timeout
      ) {
        clearTimeout(timeout_id);
        return response;
      } else {
        await new Promise((resolve) => {
          timeout_id = setTimeout(resolve, interval);
        });
        return checkStatus();
      }
    };

    return checkStatus();
  };

  /**
   * The first series of calls is to kick of an async job to regenerate the
   * assets, the second array of calls is to poll the status of the async job.
   */
  const regenerateAssets = async (
    generatedAssets: TdsGeneratedAssetSchema[],
    productId: string
  ) => {
    const genAssetsPromiseArray = generatedAssets.reduce<
      Promise<AxiosResponse<Assets>>[]
    >((acc, genAsset) => {
      if (genAsset.is_active) {
        acc.push(
          axios.post(
            endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_async(
              tenant_id,
              productId,
              genAsset.id
            ),
            null,
            {
              params: {
                return_invisible_asset: false,
                regenerate_product_asset: true,
              },
            }
          )
        );
      }
      return acc;
    }, []);

    const genAssetsToPollPromiseArray = generatedAssets.reduce<
      Promise<AxiosResponse<Assets>>[]
    >((acc, genAsset) => {
      if (genAsset.is_active) {
        acc.push(
          pollUntilFinished(
            endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_status(
              tenant_id,
              productId,
              genAsset.id
            )
          )
        );
      }
      return acc;
    }, []);

    await Promise.all(genAssetsPromiseArray);
    await Promise.all(genAssetsToPollPromiseArray);
  };

  const canRegenerateAssets = (product: PIMProduct) => {
    const { product_schema } = product;
    return (
      product.number_of_generated_assets > 0 &&
      product_schema.generated_assets.some((genAsset) => genAsset.is_active)
    );
  };

  const handleStatusUpdate = async (statusToBecome: ProductStatusType) => {
    setLoadingState(statusToBecome);

    try {
      if (statusToBecome === "published") {
        storeDispatch({ type: "SET_IS_PUBLISHING_PRODUCT", payload: true });
      }

      const { data: updatedProduct } = await axios.post<
        { status: ProductStatusType },
        AxiosResponse<PIMProduct>
      >(
        endpoints.v2_storefronts_id_or_slug_products_id_or_slug_status(
          storefront_id,
          product.product_number ?? product.id
        ),
        { status: statusToBecome }
      );
      notifySuccess(t("Product status updated successfully"));
      if (statusToBecome === "published" || statusToBecome === "discarded") {
        if (
          statusToBecome === "published" &&
          canRegenerateAssets(updatedProduct)
        ) {
          await regenerateAssets(
            updatedProduct.product_schema.generated_assets,
            updatedProduct.id
          );
          notifySuccess(t("Assets regenerated for product"));
        }
        const product_id =
          updatedProduct.product_number ?? updatedProduct.published_product_id;
        await replaceProductId(product_id);
      }
      await mutate();
      setLoadingState(undefined);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error updating the product status"),
        {
          error,
        }
      );
      setLoadingState(undefined);
    } finally {
      storeDispatch({ type: "SET_IS_PUBLISHING_PRODUCT", payload: false });
    }
  };

  const discardConfirmMessage = t(
    "You will lose all changes made to this product. Are you sure you want to continue?"
  );

  const isDisabledBasedOnStatusPermission = (status: ProductStatusType) => {
    if (
      status === "published" &&
      product.mandatory_completion_score !== null &&
      product.product_schema.show_mandatory_completion_score &&
      product.mandatory_completion_score <
        (metadata?.publish_completion_score_threshold ?? 0)
    ) {
      return true;
    }

    if (
      (status === "published" &&
        (!hasPermission("publish_products") || !product.is_publishable)) ||
      (status === "unpublished" &&
        (!hasPermission("unpublish_products") || !product.is_publishable))
    ) {
      return true;
    } else if (
      ["published", "staged", "unpublished"].includes(status) &&
      !hasPermission("modify_products")
    ) {
      return true;
    } else if (
      ["discarded", "archived"].includes(status) &&
      !hasPermission("delete_products")
    ) {
      return true;
    }

    return false;
  };

  const updateLoadingAndDisabledState = (status: ProductStatusType) => {
    if (!product.is_editable) {
      return {
        loading: loadingState === status,
        disabled: true,
      };
    } else {
      const disabledStatus = isDisabledBasedOnStatusPermission(status)
        ? true
        : Boolean(loadingState);
      return {
        loading: loadingState === status,
        disabled: disabledStatus,
      };
    }
  };

  const actionButtonDisabledMessage = (() => {
    if (!product.is_editable) {
      return t(
        "This product is assigned to 1 or more teams and cannot be edited"
      );
    }

    if (!hasPermission("publish_products") && product.status !== "published") {
      return t("You do not have permission to publish products");
    }

    if (
      !hasPermission("unpublish_products") &&
      product.status === "published"
    ) {
      return t("You do not have permission to unpublish products");
    }

    return "";
  })();

  const publishButtonDisabledMessage = (() => {
    if (!product.is_editable) {
      return t(
        "This product is assigned to 1 or more teams and cannot be edited"
      );
    }

    if (
      product.mandatory_completion_score !== null &&
      product.product_schema.show_mandatory_completion_score &&
      product.mandatory_completion_score <
        (metadata?.publish_completion_score_threshold ?? 0)
    ) {
      return t(
        "Mandatory Completion Score must be at least {{threshold}}% to publish",
        {
          threshold: metadata?.publish_completion_score_threshold,
        }
      );
    }

    if (!hasPermission("publish_products")) {
      return t("You do not have permission to publish products");
    }

    return "";
  })();

  const statusActionButtons = () => {
    switch (product.status) {
      case "draft":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="draft_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={async () => {
                await handleStatusUpdate("discarded");
                goBackToProductsList();
              }}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              buttonText={t("Delete")}
              datatip={actionButtonDisabledMessage}
              datafor="draft_tooltip"
            />
            <ReactTooltip id="draft-discard" />
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "staged":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="staged_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={() => handleStatusUpdate("discarded")}
              buttonText={t("Discard Changes")}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              datatip={actionButtonDisabledMessage}
              datafor="staged_tooltip"
            />
            <ReactTooltip id="staged_discard" />
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "published":
        return (
          <>
            <div
              data-tip={actionButtonDisabledMessage}
              data-for="action_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("unpublished")}
                loading={updateLoadingAndDisabledState("unpublished").loading}
                disabled={updateLoadingAndDisabledState("unpublished").disabled}
                style={{ marginBottom: "0px" }}
              >
                {t("Unpublish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="action_tooltip" effect="solid" />
          </>
        );
      case "unpublished":
        return (
          <>
            <PaddedDestructiveButton
              onClick={() => handleStatusUpdate("archived")}
              loading={updateLoadingAndDisabledState("archived").loading}
              disabled={updateLoadingAndDisabledState("archived").disabled}
              data-tip={actionButtonDisabledMessage}
              data-for="unpublished_tooltip"
            >
              {t("Archive")}
            </PaddedDestructiveButton>
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
                style={{ marginBottom: "0px" }}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "unpublished_staged":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="unpublished_staged_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={() => handleStatusUpdate("discarded")}
              buttonText={t("Discard Changes")}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              datatip={actionButtonDisabledMessage}
              datafor="unpublished_staged_tooltip"
            />
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "archived":
        return (
          <>
            <div
              data-tip={actionButtonDisabledMessage}
              data-for="action_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("unpublished")}
                loading={updateLoadingAndDisabledState("unpublished").loading}
                disabled={updateLoadingAndDisabledState("unpublished").disabled}
              >
                {t("Restore")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="action_tooltip" effect="solid" />
          </>
        );
      default:
        return <></>;
    }
  };

  const isLinkDisabled: boolean = product.status !== "published";

  const handleVisibilityDataFetched = (
    visibilityData: IProductVisibilityData
  ) => {
    setVisibilityData(visibilityData);
  };

  const isConnectionOn = visibilityData?.visible_storefronts.some(
    ({ slug: storefrontSlug }) => storefrontSlug === slug
  );

  const isTabDisabled: boolean = loadingState === "published";

  const get_product_tabs_length = () => {
    const tabs = document.querySelectorAll("[data-tab-type='product_tab']");
    return tabs.length;
  };

  return (
    <FullwidthPageWrapper>
      <ProductHeaderContainer ref={ref}>
        <Breadcrumb>
          <LinkUnstyled
            to={`${adminPath}/pim/products${
              sessionStorage.getItem("products_search") ?? ""
            }`}
          >
            {t("Products")}
          </LinkUnstyled>
          <div>/</div>
          <div>{product.name}</div>
        </Breadcrumb>
        <ProductHeader>
          {productCoverImage && (
            <ProductHeaderImage
              src={productCoverImage}
              alt={product.name}
              onClick={() => setShowProductImageModal(true)}
            />
          )}
          <HeaderContainer>
            <PageTitle>{product.name}</PageTitle>
            {isSmallScreen && (
              <SubheaderContainer>
                <StatusLeft
                  color={getProductStatusColor(product.status ?? "draft")}
                  text={getProductStatusText(product.status ?? "draft", t)}
                  textStyle={{ fontSize: "13px" }}
                  productStatusType="product"
                />
              </SubheaderContainer>
            )}
            <SubheaderInformationContainer>
              <SubheaderContainer>
                <TemplatesNavIcon width={16} height={16} />
                <InnerTitleText>{t("Template")}:</InnerTitleText>
                <SubheaderContentText>
                  <LinkUnstyled
                    to={`${adminPath}/pim/templates/${product.product_schema.template_id}/groups`}
                  >
                    {product.product_schema.template_name}
                  </LinkUnstyled>
                </SubheaderContentText>
              </SubheaderContainer>
              {product.pim_product_type === "variant" &&
                product.master_product && (
                  <SubheaderContainer>
                    <MasterProductIcon width={16} height={16} />
                    <InnerTitleText>{t("Master Product")}:</InnerTitleText>
                    <SubheaderContentText>
                      <LinkUnstyled
                        to={`${adminPath}/pim/products/${product.master_product.product_number}`}
                      >
                        {product.master_product.name}
                      </LinkUnstyled>
                    </SubheaderContentText>
                  </SubheaderContainer>
                )}
              <SubheaderContainer>
                <TeamsIcon width={16} height={16} />
                <InnerTitleText>{t("Team")}:</InnerTitleText>
                <SubheaderContentText>{teamName}</SubheaderContentText>
              </SubheaderContainer>
            </SubheaderInformationContainer>

            <SubheaderInformationContainer>
              {(product.product_schema.show_total_completion_score ||
                product.product_schema.show_mandatory_completion_score) && (
                <SubheaderContainer>
                  <CardCompletionBadgeIcon
                    width={16}
                    height={16}
                    stroke={theme.resolve("color_icon_secondary")}
                  />
                  <ScoreLabel>{t("Completion Score")}: </ScoreLabel>
                  {product.product_schema.show_total_completion_score && (
                    <CompletionScore
                      score={
                        product.display_status === "staged" ||
                        product.display_status === "unpublished_staged"
                          ? product.display_completion_score
                          : product.completion_score
                      }
                      type="total"
                      tooltipId="total-completion"
                      attributeCompletionValues={
                        product.attribute_completion_values
                      }
                    />
                  )}
                  {product.product_schema.show_total_completion_score &&
                    product.product_schema.show_mandatory_completion_score && (
                      <span
                        style={{
                          lineHeight: "10px",
                          fontSize: "10px",
                        }}
                      >
                        |
                      </span>
                    )}
                  {product.product_schema.show_mandatory_completion_score && (
                    <>
                      {(() => {
                        // Get the appropriate score based on display status
                        const score =
                          product.display_status === "staged" ||
                          product.display_status === "unpublished_staged"
                            ? product.display_mandatory_completion_score ??
                              product.mandatory_completion_score
                            : product.mandatory_completion_score;

                        // Guard against invalid score
                        if (typeof score !== "number") return null;

                        return (
                          <CompletionScore
                            score={score}
                            type="mandatory"
                            tooltipId="mandatory-completion"
                            attributeCompletionValues={
                              product.attribute_completion_values
                            }
                          />
                        );
                      })()}
                    </>
                  )}
                </SubheaderContainer>
              )}
              <SubheaderContainer>
                <CalendarIcon width={16} height={16} />
                <InnerTitleText>{t("Last Modified")}:</InnerTitleText>
                <SubheaderContentText>
                  {moment(product.last_modified_full.modified_at).format(
                    "MMM DD, YYYY"
                  )}
                </SubheaderContentText>
              </SubheaderContainer>
            </SubheaderInformationContainer>
          </HeaderContainer>

          <ProductHeaderActions>
            {!isSmallScreen && (
              <StatusLeft
                color={getProductStatusColor(product.status ?? "draft")}
                text={getProductStatusText(product.status ?? "draft", t)}
                textStyle={{ fontSize: "13px" }}
                productStatusType="product"
              />
            )}
            <ProductShareButton
              productId={productId}
              slug={product.slug}
              name={product.name}
              produced_by={product.produced_by}
              isLinkDisabled={isLinkDisabled}
              isConnectionOn={isConnectionOn}
            />
            {statusActionButtons()}
          </ProductHeaderActions>
        </ProductHeader>
      </ProductHeaderContainer>
      {product && (
        <ContentContainer
          pageContainerSettings={pageContainerSettings}
          productBackground={
            storefront_metadata.theme_object.designTokens?.productDetail
              ?.color_bg_surface_product?.value
          }
          headerHeight={headerHeight}
        >
          <Tabs
            key={get_product_tabs_length()}
            onChange={handleTabChange}
            index={currentTab}
          >
            <ProductTabs
              product={product}
              t={t}
              replaceProductId={replaceProductId}
              mutate={mutate}
              hasPermission={hasPermission}
              onVisibilityDataFetched={handleVisibilityDataFetched}
              isTabDisabled={isTabDisabled}
              isShowTableWithBulkAction={true}
              isShowGridAssetWithBulkAction={true}
              documentType={query.documentType || undefined}
              show_pricing_tab={show_pricing_tab}
            />
          </Tabs>
        </ContentContainer>
      )}

      {showProductImageModal && productCoverImage && (
        <Modal
          show={showProductImageModal}
          closeModal={() => setShowProductImageModal(false)}
        >
          <ModalWrapper>
            <img
              src={productCoverImage}
              alt={product.name}
              style={{
                maxWidth: fullSizeProductImage ? "auto" : "100%",
                maxHeight: fullSizeProductImage ? "auto" : "100%",
                cursor: fullSizeProductImage ? "zoom-out" : "zoom-in",
              }}
              onClick={() => setFullSizeProductImage((current) => !current)}
            />
          </ModalWrapper>
        </Modal>
      )}
    </FullwidthPageWrapper>
  );
};
